// src/pages/Performance/EvaluateAppraisalPage.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";

import {
  Box,
  CircularProgress,
  Typography,
  Stack,
  Alert,
  Button,
  Snackbar,
  Fade,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  API,
  RATING_MIN,
  RATING_MAX,
  RATING_STEP,
  safeMsg,
  extractLaravelErrors,
  STATUS_LABELS,
  STATUS_COLOR,
  getStepInstances,
  getStepLabel,
  normalizeQuestionBank,
  pick,
  detectSelfReview,
  extractAnswers,
  isMeaningfulAnswerLike,
  hasAnyMeaningfulAnswer,
  buildQuestionsFromBank,
  mergeSubmissionAnswersIntoRows,
  prefillFromSourceRows,
  buildBaselineMap,
  isRowChanged,
  roundToStep,
  clampRatingStr,
  normalizeRatingNum,
  computeOverall,
  toBool,
  resolveCurrentUser,
} from "./EvaluateAppraisal/evaluateAppraisal.utils";

import { GlassCard } from "./EvaluateAppraisal/evaluateAppraisal.styled";
import HeaderHero from "./EvaluateAppraisal/HeaderHero";
import LeftSidebar from "./EvaluateAppraisal/LeftSidebar";
import WorkflowStepsCard from "./EvaluateAppraisal/WorkflowStepsCard";
import QuestionsCard from "./EvaluateAppraisal/QuestionsCard";

/* ---------------------------------------
   Component (Container)
---------------------------------------- */
const EvaluateAppraisalPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const [appraisal, setAppraisal] = useState(null);
  const [bankQuestions, setBankQuestions] = useState([]);
  const [bankCount, setBankCount] = useState(0);

  const [selectedStepId, setSelectedStepId] = useState(null);
  const [questions, setQuestions] = useState([]);

  const [summaryComment, setSummaryComment] = useState("");
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [questionFilter, setQuestionFilter] = useState("all"); // all | unanswered | modified | excluded

  const [latestSubmission, setLatestSubmission] = useState(null);
  const [baselineByQid, setBaselineByQid] = useState({});
  const [referenceInfo, setReferenceInfo] = useState(null);
  const [prefillInfo, setPrefillInfo] = useState(null);

  // Recommendation fields (3 options + remarks)
  const [recommendation, setRecommendation] = useState({
    promotion: false,
    increment: false,
    bonus: false,
    remarks: "",
  });

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [stepLoading, setStepLoading] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  // Required + field-level errors
  const [fieldErrors, setFieldErrors] = useState({
    summary: "",
    recommendation_remarks: "",
    questions: {}, // { [questionId]: { rating?: string, comments?: string, applicability?: string } }
  });

  // Toast
  const [toast, setToast] = useState({
    open: false,
    severity: "info",
    message: "",
  });

  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  const dirtyRef = useRef(false);
  const selectedStepIdRef = useRef(null);
  const versionsCacheRef = useRef(new Map());
  const questionRefs = useRef({});

  const summaryBlockRef = useRef(null);
  const recRemarksBlockRef = useRef(null);

  // Debug: enable via ?perf_rec_debug=1 OR localStorage.setItem("perf_rec_debug","1")
  const debugEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;
    try {
      const url = new URL(window.location.href);
      const q = url.searchParams.get("perf_rec_debug");
      if (q === "1" || q === "true") return true;
      return localStorage.getItem("perf_rec_debug") === "1";
    } catch {
      return false;
    }
  }, []);

  const dbg = useCallback(
    (label, payload = null) => {
      if (!debugEnabled) return;
      // eslint-disable-next-line no-console
      console.log(`[PERF_REC] ${label}`, payload ?? "");
    },
    [debugEnabled]
  );

  useEffect(() => {
    selectedStepIdRef.current = selectedStepId;
  }, [selectedStepId]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const onOnline = () => setIsOnline(true);
    const onOffline = () => setIsOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const openToast = (message, severity = "info") => {
    setToast({ open: true, message: String(message || ""), severity });
  };

  const clearFieldErrors = () => {
    setFieldErrors({ summary: "", recommendation_remarks: "", questions: {} });
  };

  const stepsTimeline = useMemo(() => {
    const steps = appraisal ? getStepInstances(appraisal) : [];
    return (steps || [])
      .slice()
      .sort((a, b) => (a.step_order || 0) - (b.step_order || 0));
  }, [appraisal]);

  const selectedStep = useMemo(() => {
    if (!appraisal) return null;
    const steps = getStepInstances(appraisal) || [];
    if (!steps.length) return null;

    if (selectedStepId) {
      const s = steps.find((x) => Number(x.id) === Number(selectedStepId));
      if (s) return s;
    }

    const myPendingId = appraisal?.my_pending_step_id;
    if (myPendingId) {
      const s = steps.find((x) => Number(x.id) === Number(myPendingId));
      if (s) return s;
    }

    const curOrder = appraisal?.current_step_order;
    if (curOrder != null) {
      const s = steps.find((x) => Number(x.step_order) === Number(curOrder));
      if (s) return s;
    }

    return stepsTimeline[0] || null;
  }, [appraisal, selectedStepId, stepsTimeline]);

  const isSelfReview = useMemo(
    () => detectSelfReview(appraisal, selectedStep),
    [appraisal, selectedStep]
  );

  const isEditable = useMemo(() => {
    if (!appraisal || !selectedStep) return false;
    const myPendingId = appraisal?.my_pending_step_id;
    if (!myPendingId) return false;
    return Number(selectedStep.id) === Number(myPendingId);
  }, [appraisal, selectedStep]);

  /**
   * Requirement:
   * Recommendation should be hidden ONLY for the appraisee (the employee being evaluated).
   *
   * Preferred authoritative source:
   * Use backend flag: appraisal.is_appraisee_viewer (true => hide)
   *
   * Fallback if backend flag missing:
   * Compare viewer vs employee by employee_code or id.
   */
  const resolvedUser = useMemo(() => resolveCurrentUser(), []);

  const viewer = useMemo(() => {
    return (
      appraisal?.current_user ||
      appraisal?.auth_user ||
      appraisal?.me ||
      appraisal?.viewer ||
      resolvedUser ||
      null
    );
  }, [appraisal, resolvedUser]);

  const viewerCode = useMemo(() => {
    return String(
      viewer?.employee_code ??
        viewer?.employeeCode ??
        viewer?.employee?.employee_code ??
        viewer?.employee?.employeeCode ??
        resolvedUser?.employee_code ??
        resolvedUser?.employeeCode ??
        resolvedUser?.employee?.employee_code ??
        resolvedUser?.employee?.employeeCode ??
        ""
    ).trim();
  }, [viewer, resolvedUser]);

  const appraisalEmployeeCode = useMemo(() => {
    return String(
      appraisal?.employee?.employee_code ??
        appraisal?.employee?.employeeCode ??
        appraisal?.employee_code ??
        appraisal?.employeeCode ??
        ""
    ).trim();
  }, [appraisal]);

  const viewerIdNum = useMemo(() => {
    return Number(viewer?.id ?? viewer?.user?.id ?? resolvedUser?.id ?? 0) || 0;
  }, [viewer, resolvedUser]);

  const employeeUserIdNum = useMemo(() => {
    return (
      Number(
        appraisal?.employee?.id ??
          appraisal?.employee?.user_id ??
          appraisal?.employee_id ??
          0
      ) || 0
    );
  }, [appraisal]);

  const viewerIsAppraisalEmployee = useMemo(() => {
    if (!appraisal || !viewer) return false;

    if (viewerCode && appraisalEmployeeCode && viewerCode === appraisalEmployeeCode) return true;

    return !!viewerIdNum && !!employeeUserIdNum && viewerIdNum === employeeUserIdNum;
  }, [appraisal, viewer, viewerCode, appraisalEmployeeCode, viewerIdNum, employeeUserIdNum]);

  // Authoritative server flag (null if not provided).
  const serverIsAppraiseeViewer = useMemo(() => {
    const v = appraisal?.is_appraisee_viewer;
    return typeof v === "boolean" ? v : null;
  }, [appraisal]);

  const canShowRecommendations = useMemo(() => {
    if (!appraisal) return false;

    // Preferred path avoids guessing.
    if (serverIsAppraiseeViewer !== null) return !serverIsAppraiseeViewer;

    // Fallback safety.
    if (!viewer) return false;
    return !viewerIsAppraisalEmployee;
  }, [appraisal, serverIsAppraiseeViewer, viewer, viewerIsAppraisalEmployee]);

  // Debug decision logs
  useEffect(() => {
    if (!debugEnabled) return;

    const step = selectedStep;

    // eslint-disable-next-line no-console
    console.groupCollapsed(
      `[PERF_REC][decision] appraisal#${appraisal?.id ?? id} step#${step?.id ?? "-"}`
    );

    // eslint-disable-next-line no-console
    console.table({
      appraisal_id: appraisal?.id ?? null,
      selected_step_id: step?.id ?? null,
      selected_step_order: step?.step_order ?? null,
      is_editable: Boolean(isEditable),
      is_self_review: Boolean(isSelfReview),

      server_is_appraisee_viewer: serverIsAppraiseeViewer,

      viewer_present: Boolean(viewer),
      viewer_id: viewer?.id ?? null,
      viewer_employee_code: viewerCode || null,

      employee_id: appraisal?.employee?.id ?? appraisal?.employee_id ?? null,
      employee_user_id: appraisal?.employee?.user_id ?? null,
      employee_employee_code: appraisalEmployeeCode || null,

      match_by_code: Boolean(
        viewerCode && appraisalEmployeeCode && viewerCode === appraisalEmployeeCode
      ),
      match_by_id: Boolean(viewerIdNum && employeeUserIdNum && viewerIdNum === employeeUserIdNum),

      viewerIsAppraisalEmployee: Boolean(viewerIsAppraisalEmployee),
      canShowRecommendations: Boolean(canShowRecommendations),
    });

    // eslint-disable-next-line no-console
    console.log("viewer(raw):", viewer);
    // eslint-disable-next-line no-console
    console.log("employee(raw):", appraisal?.employee);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, [
    debugEnabled,
    appraisal,
    id,
    selectedStep,
    isEditable,
    isSelfReview,
    serverIsAppraiseeViewer,
    viewer,
    viewerCode,
    appraisalEmployeeCode,
    viewerIdNum,
    employeeUserIdNum,
    viewerIsAppraisalEmployee,
    canShowRecommendations,
  ]);

  // Extra safety: if we must not show it, scrub local state (prevents accidental render)
  useEffect(() => {
    if (!canShowRecommendations) {
      setRecommendation({
        promotion: false,
        increment: false,
        bonus: false,
        remarks: "",
      });
    }
  }, [canShowRecommendations]);

  const statusChipProps = useMemo(() => {
    const status = appraisal?.status || "draft";
    return {
      label: STATUS_LABELS[status] || status,
      color: STATUS_COLOR[status] || "default",
    };
  }, [appraisal]);

  const { overallAutoValue, overallAutoCount } = useMemo(() => {
    if (isSelfReview) return { overallAutoValue: "", overallAutoCount: 0 };
    const r = computeOverall(questions);
    return { overallAutoValue: r.value, overallAutoCount: r.count };
  }, [questions, isSelfReview]);

  const stats = useMemo(() => {
    const total = questions.length;

    const excluded = questions.filter((q) => String(q.applicability || "YES") === "NO").length;

    const answered = questions.filter((q) =>
      isMeaningfulAnswerLike(
        { rating: q.rating, comments: q.comments, applicability: q.applicability },
        { selfReview: isSelfReview }
      )
    ).length;

    const modified = questions.filter((q) => {
      const prev = baselineByQid?.[Number(q.questionId)];
      return !!prev?.has && isRowChanged(q, prev, { selfReview: isSelfReview });
    }).length;

    const effectiveTotal = Math.max(1, total);
    const completionProgress = Math.round((answered / effectiveTotal) * 100);

    return { total, answered, excluded, modified, completionProgress };
  }, [questions, baselineByQid, isSelfReview]);

  const latestShownText = useMemo(() => {
    if (!selectedStep) return "";
    if (stepLoading) return "Loading...";

    const hasAny = hasAnyMeaningfulAnswer(questions, { selfReview: isSelfReview });
    if (!hasAny && !referenceInfo?.fromStepOrder) return "No answers yet";

    if (referenceInfo?.fromStepOrder) {
      if (prefillInfo?.wasPrefilled) return `Carried from step #${referenceInfo.fromStepOrder}`;
      return `Reference: step #${referenceInfo.fromStepOrder}`;
    }

    return "Answers loaded";
  }, [questions, selectedStep, stepLoading, referenceInfo, prefillInfo, isSelfReview]);

  const filteredQuestions = useMemo(() => {
    if (questionFilter === "all") return questions;

    if (questionFilter === "excluded") {
      return questions.filter((q) => String(q.applicability || "YES") === "NO");
    }

    if (questionFilter === "modified") {
      return questions.filter((q) => {
        const prev = baselineByQid?.[Number(q.questionId)];
        return !!prev?.has && isRowChanged(q, prev, { selfReview: isSelfReview });
      });
    }

    // unanswered
    return questions.filter((q) => {
      const meaningful = isMeaningfulAnswerLike(
        { rating: q.rating, comments: q.comments, applicability: q.applicability },
        { selfReview: isSelfReview }
      );
      return !meaningful;
    });
  }, [questions, questionFilter, baselineByQid, isSelfReview]);

  // Robust versions parsing.
  const fetchStepLatestVersion = useCallback(async (appraisalId, stepId) => {
    const cacheKey = `${appraisalId}:${stepId}`;
    if (versionsCacheRef.current.has(cacheKey)) {
      return versionsCacheRef.current.get(cacheKey);
    }

    const { data } = await api.get(API.stepVersions(appraisalId, stepId));

    const raw =
      data?.versions ??
      data?.data?.versions ??
      data?.data?.data?.versions ??
      data?.data?.data ??
      data?.data ??
      data;

    const versions = Array.isArray(raw) ? raw : [];
    const latest = versions[0] ? versions[0] : null;

    versionsCacheRef.current.set(cacheKey, latest);
    return latest;
  }, []);

  const fetchAppraisal = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    setSubmitError("");
    setSubmitSuccess("");
    clearFieldErrors();

    try {
      versionsCacheRef.current = new Map();

      const [appRes, qRes] = await Promise.all([
        api.get(API.showAppraisal(id)),
        api.get(API.listQuestions()),
      ]);

      const appraisalData = appRes?.data || {};
      const bank = normalizeQuestionBank(qRes?.data);

      setAppraisal(appraisalData);
      setBankQuestions(bank);
      setBankCount(bank.length);

      const steps = getStepInstances(appraisalData) || [];

      setSelectedStepId((prev) => {
        if (prev && steps.some((s) => Number(s.id) === Number(prev))) return prev;

        const myPendingId = appraisalData?.my_pending_step_id;
        if (myPendingId && steps.some((s) => Number(s.id) === Number(myPendingId)))
          return myPendingId;

        const curOrder = appraisalData?.current_step_order;
        if (curOrder != null) {
          const byOrder = steps.find((s) => Number(s.step_order) === Number(curOrder));
          if (byOrder) return byOrder.id;
        }

        const first =
          steps
            .slice()
            .sort((a, b) => (a.step_order || 0) - (b.step_order || 0))[0] || null;

        return first?.id ?? null;
      });

      setSummaryComment("");
      setLatestSubmission(null);
      setQuestions([]);
      setBaselineByQid({});
      setReferenceInfo(null);
      setPrefillInfo(null);
      setExpandedQuestions({});
      setQuestionFilter("all");
      setRecommendation({ promotion: false, increment: false, bonus: false, remarks: "" });
      questionRefs.current = {};

      dbg("fetchAppraisal:loaded", {
        appraisal_id: appraisalData?.id ?? null,
        server_is_appraisee_viewer:
          typeof appraisalData?.is_appraisee_viewer === "boolean"
            ? appraisalData.is_appraisee_viewer
            : null,
        viewer_present:
          Boolean(
            appraisalData?.current_user ||
              appraisalData?.auth_user ||
              appraisalData?.me ||
              appraisalData?.viewer
          ) || false,
        employee_code:
          appraisalData?.employee?.employee_code ??
          appraisalData?.employee?.employeeCode ??
          appraisalData?.employee_code ??
          appraisalData?.employeeCode ??
          null,
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to load appraisal", err);
      setLoadError(safeMsg(err, "Failed to load appraisal"));
      setAppraisal(null);
      setBankQuestions([]);
      setBankCount(0);
      setQuestions([]);
      setSelectedStepId(null);
      setLatestSubmission(null);
      setSummaryComment("");
      setBaselineByQid({});
      setReferenceInfo(null);
      setPrefillInfo(null);
      setExpandedQuestions({});
      setQuestionFilter("all");
      setRecommendation({ promotion: false, increment: false, bonus: false, remarks: "" });
      clearFieldErrors();
      questionRefs.current = {};
    } finally {
      setLoading(false);
    }
  }, [id, dbg]);

  useEffect(() => {
    fetchAppraisal();
  }, [fetchAppraisal]);

  const lastHydrateLogKeyRef = useRef("");

  useEffect(() => {
    if (!appraisal || !selectedStepId) return;

    const steps = getStepInstances(appraisal) || [];
    const step = steps.find((s) => Number(s.id) === Number(selectedStepId)) || null;
    if (!step) return;

    dirtyRef.current = false;
    setSubmitError("");
    setSubmitSuccess("");
    setStepLoading(true);
    setReferenceInfo(null);
    setPrefillInfo(null);
    setQuestionFilter("all");
    clearFieldErrors();

    const selfReview = detectSelfReview(appraisal, step);
    const curOrder = Number(step.step_order ?? 0);

    let cancelled = false;

    (async () => {
      try {
        const baseRows = buildQuestionsFromBank(step, bankQuestions, { selfReview });

        // --------- latest for current step ----------
        let latest = null;
        try {
          latest = await fetchStepLatestVersion(appraisal.id, step.id);
        } catch {
          latest = null;
        }

        if (cancelled) return;
        if (Number(selectedStepIdRef.current) !== Number(step.id)) return;

        setLatestSubmission(latest);

        // --------- decide "evaluation chain" source step (NOT self-review) ----------
        const findByOrder = (order) =>
          stepsTimeline.find((s) => Number(s.step_order ?? 0) === Number(order)) || null;

        const desiredSourceOrder =
          selfReview ? null : curOrder === 3 ? 1 : curOrder > 1 ? curOrder - 1 : null;

        const headerSourceStep =
          (desiredSourceOrder ? findByOrder(desiredSourceOrder) : null) || null;

        let headerSourceLatest = null;
        if (headerSourceStep?.id) {
          try {
            headerSourceLatest = await fetchStepLatestVersion(appraisal.id, headerSourceStep.id);
          } catch {
            headerSourceLatest = null;
          }
        }

        // --------- compute header fields ----------
        const readRecFrom = (v) => {
          const promo =
            pick(v, "recommendation_promotion") ?? pick(v, "promotion") ?? pick(v, "rec_promotion");
          const inc =
            pick(v, "recommendation_increment") ?? pick(v, "increment") ?? pick(v, "rec_increment");
          const bonus =
            pick(v, "recommendation_bonus") ?? pick(v, "bonus") ?? pick(v, "rec_bonus");
          const remarks =
            pick(v, "recommendation_remarks") ??
            pick(v, "recommendation_remark") ??
            pick(v, "remarks") ??
            "";

          return {
            promotion: toBool(promo),
            increment: toBool(inc),
            bonus: toBool(bonus),
            remarks: remarks != null ? String(remarks) : "",
          };
        };

        if (!dirtyRef.current) {
          const curSummaryRaw =
            pick(latest, "summary_comment") ??
            pick(latest, "summary") ??
            pick(latest, "overall_summary") ??
            "";

          let nextSummary = curSummaryRaw != null ? String(curSummaryRaw || "") : "";
          if (!String(nextSummary).trim() && headerSourceLatest) {
            const srcSummaryRaw =
              pick(headerSourceLatest, "summary_comment") ??
              pick(headerSourceLatest, "summary") ??
              pick(headerSourceLatest, "overall_summary") ??
              "";
            if (srcSummaryRaw != null && String(srcSummaryRaw).trim()) {
              nextSummary = String(srcSummaryRaw);
            }
          }
          setSummaryComment(nextSummary);

          // Only hydrate recommendation if allowed.
          if (canShowRecommendations) {
            const curRec = readRecFrom(latest);
            const curRecEmpty =
              !curRec.promotion &&
              !curRec.increment &&
              !curRec.bonus &&
              !String(curRec.remarks || "").trim();

            if (curRecEmpty && headerSourceLatest) {
              const srcRec = readRecFrom(headerSourceLatest);
              setRecommendation(srcRec);
            } else {
              setRecommendation(curRec);
            }
          } else {
            setRecommendation({ promotion: false, increment: false, bonus: false, remarks: "" });
          }

          if (debugEnabled) {
            const logKey = `${appraisal.id}:${step.id}:${Boolean(canShowRecommendations)}`;
            if (lastHydrateLogKeyRef.current !== logKey) {
              lastHydrateLogKeyRef.current = logKey;

              // eslint-disable-next-line no-console
              console.groupCollapsed(
                `[PERF_REC][hydrate] appraisal#${appraisal.id} step#${step.id} canShow=${Boolean(
                  canShowRecommendations
                )}`
              );
              // eslint-disable-next-line no-console
              console.log(
                "latest(rec raw):",
                latest
                  ? {
                      recommendation_promotion: pick(latest, "recommendation_promotion"),
                      recommendation_increment: pick(latest, "recommendation_increment"),
                      recommendation_bonus: pick(latest, "recommendation_bonus"),
                      recommendation_remarks:
                        pick(latest, "recommendation_remarks") ?? pick(latest, "remarks"),
                    }
                  : null
              );
              // eslint-disable-next-line no-console
              console.log(
                "headerSourceLatest(rec raw):",
                headerSourceLatest
                  ? {
                      recommendation_promotion: pick(headerSourceLatest, "recommendation_promotion"),
                      recommendation_increment: pick(
                        headerSourceLatest,
                        "recommendation_increment"
                      ),
                      recommendation_bonus: pick(headerSourceLatest, "recommendation_bonus"),
                      recommendation_remarks:
                        pick(headerSourceLatest, "recommendation_remarks") ??
                        pick(headerSourceLatest, "remarks"),
                    }
                  : null
              );
              // eslint-disable-next-line no-console
              console.groupEnd();
            }
          }
        }

        // --------- questions rows ----------
        let rows = baseRows;
        if (latest && extractAnswers(latest).length) {
          rows = mergeSubmissionAnswersIntoRows(rows, latest, { selfReview });
        }

        // --------- reference step search ----------
        let refStep = null;
        let refRows = null;

        const getAssigneeId = (s) => Number(s?.user_id ?? s?.user?.id ?? 0);
        const currentAssigneeId = getAssigneeId(step);

        const candidates = stepsTimeline
          .filter((s) => Number(s.step_order ?? 0) < curOrder)
          .filter((s) => (selfReview ? true : !detectSelfReview(appraisal, s)))
          .slice()
          .sort((a, b) => Number(b.step_order ?? 0) - Number(a.step_order ?? 0));

        const findReference = async (list) => {
          for (const s of list) {
            const sSelf = detectSelfReview(appraisal, s);
            let sRows = buildQuestionsFromBank(s, bankQuestions, { selfReview: sSelf });

            let sLatest = s?.latest_submission || null;
            if (!sLatest) {
              try {
                sLatest = await fetchStepLatestVersion(appraisal.id, s.id);
              } catch {
                sLatest = null;
              }
            }

            if (sLatest && extractAnswers(sLatest).length) {
              sRows = mergeSubmissionAnswersIntoRows(sRows, sLatest, { selfReview: sSelf });
            }

            if (hasAnyMeaningfulAnswer(sRows, { selfReview: sSelf })) {
              return { step: s, rows: sRows };
            }
          }
          return { step: null, rows: null };
        };

        let found = { step: null, rows: null };
        if (!selfReview && currentAssigneeId) {
          const sameAssignee = candidates.filter((s) => getAssigneeId(s) === currentAssigneeId);
          found = await findReference(sameAssignee);
        }

        if (!found.step) {
          found = await findReference(candidates);
        }

        refStep = found.step;
        refRows = found.rows;

        if (refStep && refRows) {
          setReferenceInfo({
            fromStepId: refStep.id,
            fromStepOrder: refStep.step_order,
            fromLabel: getStepLabel(refStep),
            fromAssignee: refStep?.user?.name || "",
          });
        }

        const currentHasMeaningful = hasAnyMeaningfulAnswer(rows, { selfReview });

setBaselineByQid(buildBaselineMap(refRows || rows));

// Prefill only when editable and not self-review.
if (!selfReview && !currentHasMeaningful && refRows && isEditable) {
  const sourceOrder = Number(refStep?.step_order ?? 0);
  rows = prefillFromSourceRows(rows, refRows, sourceOrder, { selfReview });
  setPrefillInfo({ wasPrefilled: true, fromStepOrder: sourceOrder || null });
} else {
  // self-review stays blank (but still shows REFERENCE box via baseline)
  setPrefillInfo({ wasPrefilled: false, fromStepOrder: refStep?.step_order ?? null });
}


        if (!dirtyRef.current) {
          setQuestions(rows);
          setExpandedQuestions((prev) => {
            const next = {};
            rows.forEach((r) => {
              const qid = r.questionId;
              next[qid] = Boolean(prev?.[qid]);
            });
            return next;
          });
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error("Failed to load step data", e);
      } finally {
        if (!cancelled) setStepLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    appraisal,
    selectedStepId,
    bankQuestions,
    fetchStepLatestVersion,
    stepsTimeline,
    isEditable,
    canShowRecommendations,
    debugEnabled,
  ]);

  const handleChangeQuestionField = (questionId, field, value) => {
    if (!isEditable) return;
    dirtyRef.current = true;

    const qid = Number(questionId);

    setQuestions((prev) =>
      (prev || []).map((row) => {
        if (Number(row.questionId) !== qid) return row;

        if (field === "applicability") {
          const next = { ...row, applicability: value };
          if (String(value) === "NO") next.rating = "";
          return next;
        }

        if (field === "rating") {
          return { ...row, rating: clampRatingStr(value) };
        }

        return { ...row, [field]: value };
      })
    );

    setFieldErrors((prev) => {
      if (!qid) return prev;
      const next = { ...prev, questions: { ...(prev.questions || {}) } };
      if (!next.questions[qid]) return prev;

      const qErr = { ...(next.questions[qid] || {}) };
      if (qErr[field]) qErr[field] = "";

      const anyLeft = Object.values(qErr).some((v) => String(v || "").trim());
      if (anyLeft) next.questions[qid] = qErr;
      else delete next.questions[qid];

      return next;
    });
  };

  const handleChangeSummary = (v) => {
    if (!isEditable) return;
    dirtyRef.current = true;
    setSummaryComment(v);
    if (fieldErrors.summary) setFieldErrors((prev) => ({ ...prev, summary: "" }));
  };

  const handleRecChange = (key, value) => {
    if (!isEditable || !canShowRecommendations) return;
    dirtyRef.current = true;
    setRecommendation((prev) => ({ ...prev, [key]: value }));

    if (key === "remarks" && fieldErrors.recommendation_remarks) {
      setFieldErrors((prev) => ({ ...prev, recommendation_remarks: "" }));
    }
  };

  const handleToggleQuestion = (questionId) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  const handleExpandAll = (expand) => {
    const next = {};
    (questions || []).forEach((q) => {
      next[q.questionId] = expand;
    });
    setExpandedQuestions(next);
  };

  const registerQuestionRef = (qid, el) => {
    if (el) questionRefs.current[qid] = el;
  };

  const scrollToQuestion = (questionId) => {
    const el = questionRefs.current?.[questionId];
    if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleJumpNext = () => {
    const list = questionFilter === "unanswered" ? filteredQuestions : questions;
    const target = (list || []).find((q) => {
      const meaningful = isMeaningfulAnswerLike(
        { rating: q.rating, comments: q.comments, applicability: q.applicability },
        { selfReview: isSelfReview }
      );
      return !meaningful && String(q.applicability || "YES") !== "NO";
    });

    if (target) {
      setQuestionFilter("all");
      requestAnimationFrame(() => scrollToQuestion(target.questionId));
    }
  };

  const requirements = useMemo(() => {
    if (!isEditable) {
      return {
        missingRatings: [],
        missingSummary: false,
        missingRecRemarks: false,
        totalMissing: 0,
        firstMissingQid: null,
      };
    }

    const missingRatings = [];
    if (!isSelfReview) {
      (questions || []).forEach((q) => {
        const excluded = String(q.applicability || "YES") === "NO";
        const ratingStr = String(q.rating ?? "").trim();

        if (!excluded && !ratingStr) missingRatings.push(Number(q.questionId));

        if (!excluded && ratingStr) {
          const n = Number(ratingStr);
          const stepped = roundToStep(n, RATING_STEP);
          const okStep = Math.abs(stepped - n) < 1e-9;
          if (!Number.isFinite(n) || n < RATING_MIN || n > RATING_MAX || !okStep) {
            missingRatings.push(Number(q.questionId));
          }
        }
      });
    }

    const summaryTrim = String(summaryComment || "").trim();
    const hasAnyPerQuestionComment = (questions || []).some(
      (q) => String(q.comments || "").trim().length > 0
    );

    const missingSummary = isSelfReview
      ? !(summaryTrim.length > 0 || hasAnyPerQuestionComment)
      : summaryTrim.length === 0;

    const anyRec =
      canShowRecommendations &&
      (recommendation.promotion || recommendation.increment || recommendation.bonus);

    const recRemarksTrim = String(recommendation.remarks || "").trim();
    const missingRecRemarks = anyRec ? recRemarksTrim.length === 0 : false;

    const totalMissing =
      missingRatings.length + (missingSummary ? 1 : 0) + (missingRecRemarks ? 1 : 0);

    return {
      missingRatings,
      missingSummary,
      missingRecRemarks,
      totalMissing,
      firstMissingQid: missingRatings?.[0] ?? null,
    };
  }, [isEditable, isSelfReview, questions, summaryComment, recommendation, canShowRecommendations]);

  const validateClientSide = () => {
    const next = { summary: "", recommendation_remarks: "", questions: {} };
    let firstMissingQid = null;

    const summaryTrim = String(summaryComment || "").trim();
    const hasAnyPerQuestionComment = (questions || []).some(
      (q) => String(q.comments || "").trim().length > 0
    );

    if (isSelfReview) {
      if (!summaryTrim && !hasAnyPerQuestionComment) {
        next.summary = "Add at least one comment (a question comment or the summary).";
      }
    } else {
      if (!summaryTrim) next.summary = "Summary is required.";
    }

    if (!isSelfReview) {
      (questions || []).forEach((q) => {
        const qid = Number(q.questionId);
        const excluded = String(q.applicability || "YES") === "NO";
        const ratingStr = String(q.rating ?? "").trim();

        if (!excluded) {
          if (!ratingStr) {
            next.questions[qid] = { ...(next.questions[qid] || {}), rating: "Rating is required." };
            if (!firstMissingQid) firstMissingQid = qid;
          } else {
            const n = Number(ratingStr);
            const stepped = roundToStep(n, RATING_STEP);
            const okStep = Math.abs(stepped - n) < 1e-9;

            if (!Number.isFinite(n) || n < RATING_MIN || n > RATING_MAX) {
              next.questions[qid] = {
                ...(next.questions[qid] || {}),
                rating: `Rating must be a number between ${RATING_MIN} and ${RATING_MAX}.`,
              };
              if (!firstMissingQid) firstMissingQid = qid;
            } else if (!okStep) {
              next.questions[qid] = {
                ...(next.questions[qid] || {}),
                rating: `Rating must be in steps of ${RATING_STEP}.`,
              };
              if (!firstMissingQid) firstMissingQid = qid;
            }
          }
        }
      });
    }

    const anyRec =
      canShowRecommendations &&
      (recommendation.promotion || recommendation.increment || recommendation.bonus);
    const recRemarksTrim = String(recommendation.remarks || "").trim();
    if (anyRec && !recRemarksTrim) {
      next.recommendation_remarks =
        "Remarks are required when you select Promotion/Increment/Bonus.";
    }

    setFieldErrors(next);

    const hasAnyErrors =
      !!next.summary ||
      !!next.recommendation_remarks ||
      Object.keys(next.questions || {}).length > 0;

    if (hasAnyErrors) {
      if (next.summary && summaryBlockRef.current?.scrollIntoView) {
        summaryBlockRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (next.recommendation_remarks && recRemarksBlockRef.current?.scrollIntoView) {
        recRemarksBlockRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      } else if (firstMissingQid) {
        scrollToQuestion(firstMissingQid);
      }
    }

    return !hasAnyErrors;
  };

  const applyServerValidationErrors = (err) => {
    const errors = extractLaravelErrors(err);
    if (!errors) return false;

    const next = { summary: "", recommendation_remarks: "", questions: {} };

    Object.entries(errors).forEach(([key, val]) => {
      const msg = Array.isArray(val) ? String(val[0] ?? "") : String(val ?? "");
      if (!msg) return;

      if (key === "summary_comment" || key === "summary") {
        next.summary = msg;
        return;
      }

      if (key === "recommendation_remarks") {
        next.recommendation_remarks = msg;
        return;
      }

      if (String(key).startsWith("responses.")) {
        const m = String(key).match(/^responses\.(\d+)\.([a-zA-Z_]+)$/);
        if (m) {
          const idx = Number(m[1]);
          const field = String(m[2]);
          const qid = Number(questions?.[idx]?.questionId);
          if (qid) {
            next.questions[qid] = { ...(next.questions[qid] || {}), [field]: msg };
          }
        }
      }
    });

    setFieldErrors((prev) => {
      const merged = {
        summary: next.summary || prev.summary || "",
        recommendation_remarks: next.recommendation_remarks || prev.recommendation_remarks || "",
        questions: { ...(prev.questions || {}) },
      };
      Object.entries(next.questions || {}).forEach(([qid, qErr]) => {
        merged.questions[qid] = { ...(merged.questions[qid] || {}), ...(qErr || {}) };
      });
      return merged;
    });

    return true;
  };

  const handleSubmit = async () => {
    if (!selectedStep || !appraisal) return;

    setSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      if (!isOnline) {
        setSubmitError("You are offline. Please reconnect and try again.");
        openToast("You are offline.", "warning");
        return;
      }

      if (!isEditable) {
        setSubmitError("This step is view-only (not assigned as your pending task).");
        openToast("This step is view-only.", "warning");
        return;
      }

      const ok = validateClientSide();
      if (!ok) {
        setSubmitError("Please complete the required fields.");
        openToast("Please complete the required fields.", "warning");
        return;
      }

      const hasAnyPerQuestionComment = (questions || []).some(
        (q) => (q.comments || "").trim().length > 0
      );
      const hasSummary = (summaryComment || "").trim().length > 0;

      if (isSelfReview && !hasAnyPerQuestionComment && !hasSummary) {
        setSubmitError(
          "For Self Review, please add at least one comment (question comment or summary)."
        );
        setFieldErrors((prev) => ({
          ...prev,
          summary: prev.summary || "Add at least one comment (a question comment or the summary).",
        }));
        openToast("Please add at least one comment.", "warning");
        return;
      }

      // Do not send recommendation fields at all if not allowed.
      const recPayload = canShowRecommendations
        ? {
            recommendation_promotion: recommendation.promotion ? "YES" : "NO",
            recommendation_increment: recommendation.increment ? "YES" : "NO",
            recommendation_bonus: recommendation.bonus ? "YES" : "NO",
            recommendation_remarks: recommendation.remarks?.trim()
              ? recommendation.remarks.trim()
              : null,
          }
        : {};

      const payload = {
        responses: (questions || []).map((q) => ({
          question_id: q.questionId,
          rating: isSelfReview ? null : normalizeRatingNum(q.rating),
          comments: q.comments?.trim() ? q.comments.trim() : null,
          applicability: isSelfReview ? "YES" : q.applicability || "YES",
        })),
        summary_comment: summaryComment?.trim() ? summaryComment.trim() : null,
        overall_rating: isSelfReview
          ? null
          : overallAutoValue === ""
          ? null
          : normalizeRatingNum(overallAutoValue),
        status: "completed",
        ...recPayload,
      };

      const res = await api.post(API.submitStep(appraisal.id, selectedStep.id), payload);

      dirtyRef.current = false;
      clearFieldErrors();
      setSubmitSuccess(res?.data?.message || "Step submitted successfully.");
      openToast(res?.data?.message || "Submitted successfully.", "success");
      await fetchAppraisal();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to submit step", err);

      const hadServerErrors = applyServerValidationErrors(err);
      const msg = safeMsg(err, "Failed to submit step");

      setSubmitError(msg);

      if (hadServerErrors) openToast("Please correct the highlighted fields.", "warning");
      else openToast(msg, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = () => navigate(`/performance/appraisals/${id}`);
  const handleRefresh = () => fetchAppraisal();

  const onJumpToRequired = () => {
    if (requirements.missingSummary && summaryBlockRef.current?.scrollIntoView) {
      summaryBlockRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (requirements.missingRecRemarks && recRemarksBlockRef.current?.scrollIntoView) {
      recRemarksBlockRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (requirements.firstMissingQid) scrollToQuestion(requirements.firstMissingQid);
  };

  /* ---------- Render ---------- */

  const pageBg = `radial-gradient(1300px 660px at -5% -10%, ${alpha(
    theme.palette.primary.main,
    0.11
  )} 0%, transparent 62%),
    radial-gradient(1100px 560px at 110% 8%, ${alpha(theme.palette.info.main, 0.1)} 0%, transparent 58%),
    radial-gradient(900px 500px at 55% 110%, ${alpha(theme.palette.secondary.main, 0.08)} 0%, transparent 60%),
    linear-gradient(145deg, ${alpha(theme.palette.background.default, 0.95)} 0%, ${alpha(
    theme.palette.background.default,
    1
  )} 100%)`;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          background: theme.hr?.gradient?.page || pageBg,
          p: 3,
        }}
      >
        <Stack spacing={2.5} alignItems="center">
          <CircularProgress size={64} thickness={4} />
          <Typography variant="h6" sx={{ color: "text.secondary", fontWeight: 700 }}>
            Loading appraisal...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", justifyContent: "center" }}>
        <GlassCard sx={{ maxWidth: 720, width: "100%" }}>
          <Box sx={{ p: 4 }}>
            <Stack spacing={3} alignItems="center" textAlign="center">
              <Typography variant="h5" sx={{ fontWeight: 900, color: theme.palette.error.main }}>
                Failed to load appraisal
              </Typography>
              <Alert
                severity="error"
                sx={{
                  width: "100%",
                  borderRadius: "8px",
                  border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
                  backgroundColor: alpha(theme.palette.error.main, 0.05),
                }}
              >
                {loadError}
              </Alert>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Button
                  variant="contained"
                  onClick={fetchAppraisal}
                  startIcon={<RefreshIcon />}
                  sx={{ borderRadius: "8px", px: 3 }}
                >
                  Retry
                </Button>
                <Button variant="outlined" onClick={handleBack} sx={{ borderRadius: "8px", px: 3 }}>
                  Back to Appraisal
                </Button>
              </Stack>
            </Stack>
          </Box>
        </GlassCard>
      </Box>
    );
  }

  if (!appraisal) return null;

  const employee = appraisal.employee || {};
  const workflow = appraisal.workflow_template || appraisal.workflowTemplate || {};

  const noSelectedStep = !selectedStep;

  const submitDisabled =
    submitting ||
    !isEditable ||
    noSelectedStep ||
    bankCount === 0 ||
    !(questions || []).length ||
    (isEditable && requirements.totalMissing > 0);

  const anyRecSelected =
    canShowRecommendations &&
    (recommendation.promotion || recommendation.increment || recommendation.bonus);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: theme.hr?.gradient?.page || pageBg,
        p: { xs: 1.25, md: 1.5 },
        pb: { xs: 2.5, md: 3 },
        overflowX: "hidden",
        position: "relative",
        isolation: "isolate",
        "&::before": {
          content: '""',
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: -1,
          background: `repeating-linear-gradient(
            135deg,
            ${alpha(theme.palette.primary.main, 0.02)} 0px,
            ${alpha(theme.palette.primary.main, 0.02)} 2px,
            transparent 2px,
            transparent 16px
          )`,
          opacity: theme.palette.mode === "light" ? 0.5 : 0.2,
        },
        "&::after": {
          content: '""',
          position: "fixed",
          width: 300,
          height: 300,
          right: -120,
          bottom: -120,
          pointerEvents: "none",
          zIndex: -1,
          borderRadius: "8px",
          background: `radial-gradient(circle,
            ${alpha(theme.palette.primary.main, 0.2)} 0%,
            ${alpha(theme.palette.primary.main, 0.03)} 45%,
            transparent 72%)`,
          filter: "blur(8px)",
        },
      }}
    >
      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4200}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((p) => ({ ...p, open: false }))}
          severity={toast.severity}
          sx={{ borderRadius: "8px", border: `1px solid ${alpha(theme.palette.divider, 0.18)}` }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

      <Fade in timeout={420}>
        <Box
          sx={{
            maxWidth: 1320,
            mx: "auto",
            borderRadius: "8px",
            p: { xs: 1, md: 1.25 },
            background: alpha(theme.palette.background.default, theme.palette.mode === "light" ? 0.82 : 0.55),
            border: `1px solid ${theme.hr?.line?.soft || alpha(theme.palette.divider, 0.18)}`,
            boxShadow: "none",
          }}
        >
          <HeaderHero
            appraisal={appraisal}
            workflow={workflow}
            employee={employee}
            statusChipProps={statusChipProps}
            latestShownText={latestShownText}
            latestSubmission={latestSubmission}
            stats={stats}
            isEditable={isEditable}
            isOnline={isOnline}
            requirements={requirements}
            onBack={handleBack}
            onRefresh={handleRefresh}
          />

          <Box
            sx={{
              display: "flex",
              gap: { xs: 1.5, md: 1.5 },
              flexDirection: { xs: "column", md: "row" },
              alignItems: "flex-start",
              pb: { xs: 1.5, md: 1.5 },
            }}
          >
            {/* LEFT */}
            <LeftSidebar
              appraisal={appraisal}
              employee={employee}
              workflow={workflow}
              selectedStep={selectedStep}
              noSelectedStep={noSelectedStep}
              bankCount={bankCount}
              isEditable={isEditable}
              isSelfReview={isSelfReview}
              referenceInfo={referenceInfo}
              prefillInfo={prefillInfo}
              overallAutoValue={overallAutoValue}
              overallAutoCount={overallAutoCount}
              summaryComment={summaryComment}
              onSummaryChange={handleChangeSummary}
              recommendation={recommendation}
              onRecChange={handleRecChange}
              canShowRecommendations={canShowRecommendations}
              anyRecSelected={anyRecSelected}
              fieldErrors={fieldErrors}
              requirements={requirements}
              summaryBlockRef={summaryBlockRef}
              recRemarksBlockRef={recRemarksBlockRef}
            />

            {/* RIGHT */}
            <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
              <Stack spacing={1.5}>
                <WorkflowStepsCard
                  stepsTimeline={stepsTimeline}
                  selectedStep={selectedStep}
                  appraisal={appraisal}
                  onSelectStep={(stepId) => setSelectedStepId(stepId)}
                  onExpandAll={() => handleExpandAll(true)}
                  onCollapseAll={() => handleExpandAll(false)}
                />

                {submitError && (
                  <Alert
                    severity="error"
                    onClose={() => setSubmitError("")}
                    sx={{
                      borderRadius: "8px",
                      border: `1px solid ${alpha(theme.palette.error.main, 0.18)}`,
                      backgroundColor: alpha(theme.palette.error.main, 0.05),
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Submission failed
                    </Typography>
                    <Typography variant="body2">{submitError}</Typography>
                  </Alert>
                )}

                {submitSuccess && (
                  <Alert
                    severity="success"
                    onClose={() => setSubmitSuccess("")}
                    sx={{
                      borderRadius: "8px",
                      border: `1px solid ${alpha(theme.palette.success.main, 0.18)}`,
                      backgroundColor: alpha(theme.palette.success.main, 0.05),
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 900 }}>
                      Submitted
                    </Typography>
                    <Typography variant="body2">{submitSuccess}</Typography>
                  </Alert>
                )}

                <QuestionsCard
                  noSelectedStep={noSelectedStep}
                  bankCount={bankCount}
                  stepLoading={stepLoading}
                  questions={questions}
                  filteredQuestions={filteredQuestions}
                  expandedQuestions={expandedQuestions}
                  onToggleQuestion={handleToggleQuestion}
                  registerQuestionRef={registerQuestionRef}
                  questionFilter={questionFilter}
                  onChangeFilter={(v) => setQuestionFilter(v)}
                  onJumpNext={handleJumpNext}
                  isSelfReview={isSelfReview}
                  isEditable={isEditable}
                  baselineByQid={baselineByQid}
                  referenceInfo={referenceInfo}
                  fieldErrors={fieldErrors}
                  requirements={requirements}
                  onJumpToRequired={onJumpToRequired}
                  onChangeQuestionField={handleChangeQuestionField}
                  onFocusQuestion={(qid) => scrollToQuestion(qid)}
                  submitting={submitting}
                  submitDisabled={submitDisabled}
                  onBack={handleBack}
                  onSubmit={handleSubmit}
                  overallAutoValue={overallAutoValue}
                />

                {/* Footer info */}
                <Box
                  sx={{
                    p: { xs: 1.25, md: 1.5 },
                    borderRadius: "8px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
                    background: `linear-gradient(135deg,
                      ${alpha(theme.palette.background.paper, 0.78)} 0%,
                      ${alpha(theme.palette.background.paper, 0.62)} 100%)`,
                    boxShadow: "none",
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={1.5}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Appraisal Info
                      </Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 0.25 }}>
                        <Typography variant="caption" color="text.secondary">
                          ID: {appraisal.id}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Workflow: {workflow?.name || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Employee: {employee.name || "-"}
                        </Typography>
                      </Stack>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {isSelfReview ? "Self Review Mode" : "Evaluation Mode"}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

export default EvaluateAppraisalPage;
